import { germanStringToNumber } from 'ComponentLibrary/src/common/money'
import { decimalToLocalizedString } from 'ComponentLibrary/src/common/numbers'
import { ChangeEventHandler, forwardRef, Ref, useEffect, useRef, useState } from 'react'
import { Control, FieldPathByValue, FieldPathValue, FieldValues, UseControllerProps, useController, useWatch } from 'react-hook-form'
import { isRequiredRule, TextInput, TextInputProps } from '../TextInput/TextInput'

export type NumberInputFormat = 'decimal' | 'integer' | 'year'

export type NumberInputProps = Omit<TextInputProps, 'defaultValue' | 'inputMode' | 'onChange' | 'type' | 'value'> & {
    defaultValue?: number
    format?: NumberInputFormat
    maximumFractionDigits?: number
    minimumFractionDigits?: number
    onValueChange?: (value: number | undefined) => void
    value?: number
}

export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(function NumberInput(
    { maximumFractionDigits = 5, ...props },
    ref
) {
    return (
        <NumberInputBase
            {...props}
            inputRef={ref}
            maximumFractionDigits={maximumFractionDigits}
            maximumInputFractionDigits={maximumFractionDigits}
        />
    )
})

type NumberInputBaseProps = NumberInputProps & {
    areValuesEquivalent?: (firstValue: number | undefined, secondValue: number | undefined) => boolean
    inputRef: Ref<HTMLInputElement>
    maximumInputFractionDigits?: number
    maximumFractionDigits: number
}

export function NumberInputBase({
    areValuesEquivalent = Object.is,
    inputRef,
    maximumInputFractionDigits,
    maximumFractionDigits,
    minimumFractionDigits: minimumFractionDigitsOverride,
    ...props
}: NumberInputBaseProps) {
    const { defaultValue, format = 'decimal', onBlur, onValueChange, textAlign = 'right', value, ...textInputProps } = props
    const isControlled = Object.prototype.hasOwnProperty.call(props, 'value')
    const isWholeNumberFormat = format === 'integer' || format === 'year'
    const resolvedMaximumFractionDigits = isWholeNumberFormat ? 0 : maximumFractionDigits
    const minimumFractionDigits = isWholeNumberFormat ? 0 : (minimumFractionDigitsOverride ?? 2)
    const initialValue = isControlled ? value : defaultValue
    const [inputValue, setInputValue] = useState(() =>
        formatNumberValue(initialValue, {
            format,
            maximumFractionDigits: resolvedMaximumFractionDigits,
            minimumFractionDigits,
        })
    )
    const pendingValueRef = useRef<{ hasValue: boolean; value: number | undefined }>({
        hasValue: false,
        value: undefined,
    })

    useEffect(() => {
        if (!isControlled) {
            return
        }

        if (pendingValueRef.current.hasValue && areValuesEquivalent(pendingValueRef.current.value, value)) {
            pendingValueRef.current.hasValue = false
            return
        }

        pendingValueRef.current.hasValue = false
        const formattedValue = formatNumberValue(value, {
            format,
            maximumFractionDigits: resolvedMaximumFractionDigits,
            minimumFractionDigits,
        })
        if (inputValue !== formattedValue) {
            setInputValue(formattedValue)
        }
    }, [areValuesEquivalent, format, inputValue, isControlled, minimumFractionDigits, resolvedMaximumFractionDigits, value])

    const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
        const rawInputValue = event.target.value
        const rawValue = parseNumberInput(rawInputValue)
        const fractionLimitedInputValue =
            maximumInputFractionDigits !== undefined && rawValue !== undefined && !Number.isNaN(rawValue)
                ? truncateFractionDigits(rawInputValue, maximumInputFractionDigits)
                : rawInputValue
        const nextValue = fractionLimitedInputValue === rawInputValue ? rawValue : parseNumberInput(fractionLimitedInputValue)
        const nextInputValue =
            isWholeNumberFormat && nextValue !== undefined && !Number.isNaN(nextValue)
                ? formatNumberValue(nextValue, {
                      format,
                      maximumFractionDigits: resolvedMaximumFractionDigits,
                      minimumFractionDigits,
                  })
                : fractionLimitedInputValue

        setInputValue(nextInputValue)

        if (isControlled && onValueChange) {
            pendingValueRef.current = {
                hasValue: true,
                value: nextValue,
            }
        }
        onValueChange?.(nextValue)
    }

    return (
        <TextInput
            {...textInputProps}
            inputMode={isWholeNumberFormat ? 'numeric' : 'decimal'}
            onBlur={(event) => {
                const parsedNumber = parseNumberInput(inputValue)
                if (parsedNumber !== undefined && !Number.isNaN(parsedNumber)) {
                    setInputValue(
                        formatNumberValue(parsedNumber, {
                            format,
                            maximumFractionDigits: resolvedMaximumFractionDigits,
                            minimumFractionDigits,
                        })
                    )
                }
                onBlur?.(event)
            }}
            onChange={handleChange}
            ref={inputRef}
            textAlign={textAlign}
            value={inputValue}
        />
    )
}

type NumberFieldPath<TFieldValues extends FieldValues> = FieldPathByValue<TFieldValues, number | undefined>

type FormNumberInputRules<TFieldValues extends FieldValues, TName extends NumberFieldPath<TFieldValues>> = Omit<
    NonNullable<UseControllerProps<TFieldValues, TName>['rules']>,
    'maxLength' | 'minLength' | 'pattern'
>

export type FormNumberInputProps<
    TFieldValues extends FieldValues,
    TName extends NumberFieldPath<TFieldValues> = NumberFieldPath<TFieldValues>,
> = Omit<NumberInputProps, 'defaultValue' | 'errorMessage' | 'name' | 'onValueChange' | 'required' | 'value'> & {
    control: Control<TFieldValues>
    name: TName
    rules?: FormNumberInputRules<TFieldValues, TName>
}

export function FormNumberInput<
    TFieldValues extends FieldValues,
    TName extends NumberFieldPath<TFieldValues> = NumberFieldPath<TFieldValues>,
>({ control, format = 'decimal', name, onBlur, rules, ...numberInputProps }: FormNumberInputProps<TFieldValues, TName>) {
    const { validate, ...otherRules } = rules ?? {}
    const { field, fieldState } = useController({
        control,
        name,
        rules: {
            ...otherRules,
            validate:
                validate ??
                ((fieldValue: FieldPathValue<TFieldValues, TName>) => {
                    if (fieldValue === undefined) {
                        return true
                    }
                    if (
                        typeof fieldValue !== 'number' ||
                        Number.isNaN(fieldValue) ||
                        ((format === 'integer' || format === 'year') && !Number.isInteger(fieldValue))
                    ) {
                        return 'Bitte geben Sie eine Zahl ein'
                    }
                    return true
                }),
        },
    })
    const value = useWatch({ control, exact: true, name })

    return (
        <NumberInput
            {...numberInputProps}
            errorMessage={fieldState.error ? (fieldState.error.message?.toString() ?? '') : undefined}
            format={format}
            name={field.name}
            onBlur={(event) => {
                field.onBlur()
                onBlur?.(event)
            }}
            onValueChange={field.onChange}
            ref={field.ref}
            required={isRequiredRule(rules?.required)}
            value={value}
        />
    )
}

function parseNumberInput(inputValue: string) {
    if (inputValue === '') {
        return undefined
    }

    return germanStringToNumber(inputValue)
}

function formatNumberValue(
    value: number | undefined,
    {
        format,
        maximumFractionDigits,
        minimumFractionDigits,
    }: {
        format: NumberInputFormat
        maximumFractionDigits: number
        minimumFractionDigits: number
    }
) {
    if (value === undefined || Number.isNaN(value)) {
        return ''
    }

    if (format === 'year') {
        return `${value}`
    }

    return decimalToLocalizedString(value, format === 'integer', maximumFractionDigits, minimumFractionDigits)
}

function truncateFractionDigits(inputValue: string, maximumFractionDigits: number) {
    const decimalIndex = inputValue.indexOf(',')
    if (decimalIndex === -1) {
        return inputValue
    }

    const decimalPart = inputValue.substring(decimalIndex + 1).replace(/\D/g, '')
    return inputValue.substring(0, decimalIndex + 1) + decimalPart.slice(0, maximumFractionDigits)
}
